<template>
  <div class="publications-form">
    <div class="section-header d-flex justify-content-between align-items-center mb-4">
      <h5 class="section-title">Publications ({{ localForm.publications.length }})</h5>
      <div class="d-flex align-items-center gap-2">
        <button
          type="button"
          class="btn btn-outline-info btn-sm"
          :disabled="!orcidId || syncingOrcid"
          :title="orcidId ? `Sync publications from ORCID iD ${orcidId}` : 'Save your ORCID iD in Personal Details first'"
          @click="syncOrcidPublications"
        >
          <i class="bi" :class="syncingOrcid ? 'bi-arrow-repeat' : 'bi-cloud-download'"></i>
          {{ syncingOrcid ? 'Syncing…' : 'Sync from ORCID' }}
        </button>
        <button type="button" class="btn btn-outline-primary btn-sm" @click="addPublication">
          <i class="bi bi-plus me-1"></i>Add Publication
        </button>
      </div>
    </div>

    <div v-if="orcidSyncMessage" class="alert mt-2" :class="orcidSyncError ? 'alert-danger' : 'alert-success'">
      {{ orcidSyncMessage }}
    </div>

    <div v-if="publicationDraft" class="publication-card mb-4 border rounded bg-light-subtle">
      <div class="card-body mt-3">
        <div class="mb-3">
          <label class="form-label">Title</label>
          <input ref="newPublicationTitleInput" type="text" class="form-control" :class="{ 'field-invalid': isPublicationFieldInvalid(publicationDraft, 'title', null, true) }" v-model="publicationDraft.title" @input="handlePublicationDraftInput" placeholder="Publication title">
        </div>
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Journal</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isPublicationFieldInvalid(publicationDraft, 'journal', null, true) }" v-model="publicationDraft.journal" @input="handlePublicationDraftInput" placeholder="Journal name">
          </div>
          <div class="col-md-6">
            <label class="form-label">Date</label>
            <input type="date" class="form-control" :class="{ 'field-invalid': isPublicationFieldInvalid(publicationDraft, 'date', null, true) }" v-model="publicationDraft.date" @input="handlePublicationDraftInput">
          </div>
        </div>
        <div class="form-group mb-3">
          <label class="form-label">Keywords</label>
          <input type="text" class="form-control" v-model="publicationDraft.keywords" @input="handlePublicationDraftInput" placeholder="Keywords, separated by commas">
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewPublication">Cancel</button>
          <button type="button" class="btn btn-outline-primary" @click="saveNewPublication">Save</button>
        </div>
      </div>
    </div>

    <div v-for="(publication, index) in localForm.publications" :key="publicationKey(publication, index)" class="publication-card mb-4">
      <div
        class="publication-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="togglePublication(publicationKey(publication, index))"
        @keydown.enter.prevent="togglePublication(publicationKey(publication, index))"
        @keydown.space.prevent="togglePublication(publicationKey(publication, index))"
      >
        <h6 class="publication-header mb-0">{{ publicationHeaderTitles[publicationKey(publication, index)] }}</h6>
        <i class="bi" :class="isPublicationExpanded(publicationKey(publication, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>

      <div v-if="isPublicationExpanded(publicationKey(publication, index))" class="card-body mt-3">

        <!-- Title -->
        <div class="mb-3">
          <label class="form-label">Title</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isPublicationFieldInvalid(publication, 'title', index) }"
            v-model="publication.title"
            placeholder="Publication title"
            @input="markPublicationDirty(index)"
          >
          <div v-if="serverErrorFor(index, 'title')" class="invalid-hint">
            {{ serverErrorFor(index, 'title') }}
          </div>
        </div>

        <!-- Authors Section -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="form-label mb-0" style="color: #495057;  border-bottom: 1px solid #dee2e6;  padding-bottom: 0.5rem;">Authors</h6>
            <button type="button" class="btn btn-outline-primary btn-sm" @click="addAuthor(index)">
              <i class="bi bi-plus me-1"></i>ADD
            </button>
          </div>

          <div v-for="(author, authorIndex) in publication.authors" :key="`author-${index}-${authorIndex}`" class="nested-item mb-3">
            <div class="nested-header d-flex justify-content-between align-items-center mb-2">
              <span class="nested-title">Author {{ authorIndex + 1 }}</span>
              <button type="button" class="btn btn-sm btn-link text-danger p-1" @click="removeAuthor(index, authorIndex)">
                <i class="bi bi-x"></i>
              </button>
            </div>
            <div class="row">
              <div class="col-md-6">
                <label class="form-label">Name</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="author.name"
                  placeholder="Author name"
                  @input="markPublicationDirty(index)"
                >
              </div>
              <div class="col-md-6">
                <label class="form-label">Citation</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="author.citation"
                  placeholder="Author citation"
                  @input="markPublicationDirty(index)"
                >
              </div>
            </div>
          </div>
        </div>

        <!-- External Identifiers Section -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="form-label mb-0" style="color: #495057;  border-bottom: 1px solid #dee2e6;  padding-bottom: 0.5rem;">External Identifiers</h6>
            <button type="button" class="btn btn-outline-primary btn-sm" @click="addExternalIdentifier(index)">
              <i class="bi bi-plus me-1"></i>ADD
            </button>
          </div>

          <div v-for="(identifier, identifierIndex) in publication.externalIdentifiers" :key="`identifier-${index}-${identifierIndex}`" class="nested-item mb-3">
            <div class="nested-header d-flex justify-content-between align-items-center mb-2">
              <span class="nested-title">External Identifier {{ identifierIndex + 1 }}</span>
              <button type="button" class="btn btn-sm btn-link text-danger p-1" @click="removeExternalIdentifier(index, identifierIndex)">
                <i class="bi bi-x"></i>
              </button>
            </div>
            <div class="row">
              <div class="col-md-3">
                <label class="form-label">Ext. Identifier e.g. PMID</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="identifier.identifier"
                  placeholder="PMID"
                  @input="markPublicationDirty(index)"
                >
              </div>
              <div class="col-md-3">
                <label class="form-label">External ID</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="identifier.externalId"
                  placeholder="12345678"
                  @input="markPublicationDirty(index)"
                >
              </div>
              <div class="col-md-3">
                <label class="form-label">Full URL</label>
                <input
                  type="url"
                  class="form-control"
                  v-model="identifier.fullUrl"
                  placeholder="https://..."
                  @input="markPublicationDirty(index)"
                >
              </div>
              <div class="col-md-3">
                <label class="form-label">Relationship</label>
                <select
                  class="form-select"
                  v-model="identifier.relationship"
                  @change="markPublicationDirty(index)"
                >
                  <option value="">Select relationship</option>
                  <option value="Self">Self</option>
                  <option value="Part of">Part of</option>
                  <option value="Funded by">Funded by</option>
                  <option value="Version of">Version of</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Journal and Date -->
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Journal</label>
            <input
              type="text"
              class="form-control"
              :class="{ 'field-invalid': isPublicationFieldInvalid(publication, 'journal', index) }"
              v-model="publication.journal"
              placeholder="Journal name"
              @input="markPublicationDirty(index)"
            >
            <div v-if="serverErrorFor(index, 'journal')" class="invalid-hint">
              {{ serverErrorFor(index, 'journal') }}
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label">Date</label>
            <input
              type="date"
              class="form-control"
              :class="{ 'field-invalid': isPublicationFieldInvalid(publication, 'date', index) }"
              v-model="publication.date"
              @input="markPublicationDirty(index)"
            >
            <!-- A legacy date the browser cannot show in a date input leaves the
                 field blank, so say what is actually stored and why it failed. -->
            <div v-if="serverErrorFor(index, 'date')" class="invalid-hint">
              {{ serverErrorFor(index, 'date') }}
            </div>
          </div>
        </div>

        <!-- Keywords -->
        <div class="form-group mb-3">
          <label class="form-label">Keywords</label>
          <input
            type="text"
            class="form-control"
            v-model="publication.keywords"
            placeholder="Keywords, separated by commas"
            @input="markPublicationDirty(index)"
          >
        </div>

        <!-- Content -->
        <div class="form-group">
          <label class="form-label">Abstract</label>
          <div class="content-editor">
            <div class="editor-toolbar">
              <select class="form-select form-select-sm me-2" style="width: auto; display: inline-block;">
                <option value="normal">Normal</option>
                <option value="heading">Heading</option>
                <option value="subheading">Subheading</option>
              </select>
              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                      @click="format('bold')">
                <i class="bi bi-type-bold"></i>
              </button>

              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                      @click="format('italic')">
                <i class="bi bi-type-italic"></i>
              </button>

              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                      @click="format('underline')">
                <i class="bi bi-type-underline"></i>
              </button>

              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                      @click="format('insertUnorderedList')">
                <i class="bi bi-list-ul"></i>
              </button>

              <button type="button" class="btn btn-sm btn-outline-secondary me-1"
                      @click="format('insertOrderedList')">
                <i class="bi bi-list-ol"></i>
              </button>
            </div>
            <div
              class="form-control content-textarea"
              contenteditable="true"
              :ref="`editor-${index}`"
              @focus="isEditingContent = true"
              @blur="isEditingContent = false"
              @input="onEditorInput(index)"
              @paste="onEditorPaste($event, index)"
            ></div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-12 d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!publicationDirty[index]" @click="savePublication(index)">
              <i class="bi bi-check me-1"></i>Save
            </button>
            <button type="button" class="btn btn-sm btn-outline-danger" @click="removePublication(index)">
              <i class="bi bi-x me-1"></i>Remove
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="localForm.publications.length === 0" class="empty-state text-center py-5">
      <i class="bi bi-journal-text text-muted" style="font-size: var(--font-size-4xl);"></i>
      <p class="text-muted mt-3 mb-0">No publications added yet. Click the Add Publication button to get started.</p>
    </div>
  </div>
</template>

<script>
import ProfileApiService from '@/services/profile/ProfileApiService'
import { htmlFromClipboard } from '@/utils/pastedHtml'

export default {
  name: 'PublicationsForm',
  props: {
    formData: {
      type: Object,
      required: true
    },
    profileData: {
      type: Object,
    },
    // Server validation errors keyed as Laravel sends them,
    // e.g. { 'publications.0.date': ['Publication 1: ...'] }
    serverErrors: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update', 'pending-state-change'],
  data() {
    return {
      localForm: this.initializeLocalForm(),
      isEditingContent: false,
      showDebug: false,
      expandedPublicationIds: [],
      initialized: false,
      publicationDraft: null,
      publicationDraftAttempted: false,
      publicationDirty: {},
      publicationAttempted: {},
      newPublicationTitleInput: null,
      syncingOrcid: false,
      orcidSyncMessage: '',
      orcidSyncError: false
    }
  },
  computed: {
    fellowDetailsPublications() {
      return this.profileData?.data?.publications || []
    },
    orcidId() {
      return this.profileData?.data?.personal?.orcidId || ''
    },
    hasPendingChanges() {
      return Boolean(this.publicationDraft)
    },
    publicationHeaderTitles() {
      return (this.localForm.publications || []).reduce((titles, publication, index) => {
        const key = this.publicationKey(publication, index)
        let title = this.normalizeHeaderText(publication?.title)
        const journal = this.normalizeHeaderText(publication?.journal)
        const year = this.getYear(publication?.date)

        if (!title && !journal && !year) {
          titles[key] = `New Publication ${index + 1}`
          return titles
        }

        // Truncate title to 50 characters to prevent line wrapping
        if (title && title.length > 50) {
          title = title.substring(0, 50) + '...'
        }

        titles[key] = [title, journal, year].filter(Boolean).join(' | ')
        return titles
      }, {})
    }
  },
  watch: {
    formData: {
      handler(newData) {
        if (!this.shouldSyncFromFormData(newData)) return

        const previousExpandedIds = [...this.expandedPublicationIds]
        this.localForm = this.initializeLocalForm(newData)

        if (!this.initialized) {
          this.initializeExpandedPublicationIds()
          this.initialized = true
        } else {
          this.reconcileExpandedPublicationIds(previousExpandedIds)
        }

        if (!this.isEditingContent) {
          this.hydrateEditors()
        }
      },
      deep: true
    },

    profileData: {
      handler() {
        if (!this.shouldSyncFromProfileData()) return

        const previousExpandedIds = [...this.expandedPublicationIds]
        this.localForm = this.initializeLocalForm(this.formData)

        if (!this.initialized) {
          this.initializeExpandedPublicationIds()
          this.initialized = true
        } else {
          this.reconcileExpandedPublicationIds(previousExpandedIds)
        }

        if (!this.isEditingContent) {
          this.hydrateEditors()
        }
      },
      deep: true
    },
    hasPendingChanges: {
      handler(isPending) {
        this.$emit('pending-state-change', isPending)
      },
      immediate: true
    },
  },
  methods: {
    format(command) {
      document.execCommand(command, false, null)
    },

    onEditorPaste(event, index) {
      event.preventDefault()

      const clipboard = event.clipboardData || window.clipboardData
      const html = htmlFromClipboard(clipboard)

      if (html) {
        document.execCommand('insertHTML', false, html)
      } else {
        document.execCommand('insertText', false, clipboard.getData('text/plain'))
      }

      this.onEditorInput(index)
    },

    onEditorInput(index) {
      const ref = this.$refs[`editor-${index}`]
      const editor = Array.isArray(ref) ? ref[0] : ref
      if (!editor) return

      this.localForm.publications[index].content = editor.innerHTML
      this.markPublicationDirty(index)
    },

    hydrateEditors() {
      this.$nextTick(() => {
        this.localForm.publications.forEach((pub, index) => {
          const ref = this.$refs[`editor-${index}`]
          const editor = Array.isArray(ref) ? ref[0] : ref
          if (!editor) return

          editor.innerHTML = pub.content || ''
        })
      })
    },

    initializeLocalForm(data = null) {
      const sourceData = data || this.formData
      const previousPublications = this.localForm?.publications || []

      // Priority 1: Use existing form data if it has publications
      if (sourceData?.publications && Array.isArray(sourceData.publications) && sourceData.publications.length > 0) {
        return {
          publications: sourceData.publications.map((publication, index) => ({
            id: publication.id || previousPublications[index]?.id || null,
            _localKey: previousPublications[index]?._localKey || publication._localKey || this.generateLocalKey(),
            _isNew: previousPublications[index]?._isNew ?? publication._isNew ?? false,
            title: publication.title || '',
            authors: Array.isArray(publication.authors) ? publication.authors : [],
            externalIdentifiers: Array.isArray(publication.externalIdentifiers) ? publication.externalIdentifiers : [],
            journal: publication.journal || '',
            keywords: publication.keywords || '',
            date: publication.date || '',
            content: publication.content || ''
          }))
        }
      }

      // Priority 2: Extract from API response if available
      const fellowDetailsPublications = this.fellowDetailsPublications
      if (Array.isArray(fellowDetailsPublications) && fellowDetailsPublications.length > 0) {
        return {
          publications: fellowDetailsPublications.map((publication, index) => ({
            id: publication.id || previousPublications[index]?.id || null,
            _localKey: previousPublications[index]?._localKey || publication._localKey || this.generateLocalKey(),
            _isNew: previousPublications[index]?._isNew ?? publication._isNew ?? false,
            title: publication.title || '',
            authors: Array.isArray(publication.authors) ? publication.authors : [],
            externalIdentifiers: Array.isArray(publication.externalIdentifiers) ? publication.externalIdentifiers : [],
            journal: publication.journal || '',
            keywords: publication.keywords || '',
            date: publication.date || '',
            content: publication.content || ''
          }))
        }
      }

      // Priority 3: Return empty array
      return {
        publications: []
      }
    },

    updateParent() {
      this.$emit('update', {
        ...this.localForm,
        publications: (this.localForm.publications || []).map(({ _localKey, _isNew, ...publication }) => ({
          ...publication
        }))
      })
    },

    /**
     * Publications without the client-side bookkeeping keys.
     *
     * _localKey/_isNew are ours, not the API's. Sending them makes an otherwise
     * untouched legacy row look edited to the server, which then validates it
     * strictly and rejects it.
     */
    publicationsForApi() {
      return (this.localForm.publications || []).map(
        // eslint-disable-next-line no-unused-vars
        ({ _localKey, _isNew, ...publication }) => ({ ...publication })
      )
    },

    generateLocalKey() {
      return `${Date.now()}-${Math.random()}`
    },

    addPublication() {
      if (this.publicationDraft) {
        this.$nextTick(() => {
          this.$refs.newPublicationTitleInput?.focus()
        })
        return
      }

      this.publicationDraft = {
        id: null,
        _localKey: this.generateLocalKey(),
        _isNew: true,
        title: '',
        authors: [],
        externalIdentifiers: [],
        journal: '',
        keywords: '',
        date: '',
        content: ''
      }

      this.$nextTick(() => {
        this.$refs.newPublicationTitleInput?.focus()
      })
    },

    cancelNewPublication() {
      this.publicationDraft = null
    },

    isPublicationValid(publication) {
      if (!publication) return false

      return Boolean(
        String(publication.title || '').trim() &&
        String(publication.journal || '').trim() &&
        String(publication.date || '').trim()
      )
    },

    isPublicationFieldInvalid(publication, field, index = null, isDraft = false) {
      // A rejected save marks the exact row the server complained about, so the
      // fellow can see which publication is blocking them.
      if (!isDraft && this.serverErrorFor(index, field)) return true

      const shouldValidate = isDraft ? this.publicationDraftAttempted : Boolean(this.publicationAttempted[index])
      if (!shouldValidate) return false

      if (field === 'title') return !String(publication?.title || '').trim()
      if (field === 'journal') return !String(publication?.journal || '').trim()
      if (field === 'date') return !String(publication?.date || '').trim()
      return false
    },

    /**
     * The server's message for one row/field, if the last save was rejected.
     */
    serverErrorFor(index, field) {
      if (index === null || index === undefined) return null

      const messages = this.serverErrors?.[`publications.${index}.${field}`]

      return Array.isArray(messages) ? messages[0] : (messages || null)
    },

    handlePublicationDraftInput() {
      if (this.publicationDraftAttempted && this.isPublicationValid(this.publicationDraft)) {
        this.publicationDraftAttempted = false
      }
    },

    async saveNewPublication() {
      if (!this.isPublicationValid(this.publicationDraft)) {
        this.publicationDraftAttempted = true
        return
      }

      try {
        this.localForm.publications.unshift({ ...this.publicationDraft })
        const publicationsData = { publications: this.publicationsForApi() }
        const response = await ProfileApiService.updateProfile(publicationsData)

        // Refresh form data with latest from API response
        if (response?.data?.publications) {
          this.localForm.publications = response.data.publications
        }

        const key = this.publicationKey(this.localForm.publications[0], 0)
        if (key && !this.expandedPublicationIds.includes(key)) {
          this.expandedPublicationIds.push(key)
        }
        this.publicationDraft = null
        this.updateParent()
      } catch (error) {
        console.error('Error saving new publication:', error)
        this.$emit('error', 'Failed to save publication. Please try again.')
        this.localForm.publications.shift()
      }
    },

    markPublicationDirty(index) {
      this.publicationDirty = {
        ...this.publicationDirty,
        [index]: true
      }

      if (typeof index === 'number' && this.publicationAttempted[index] && this.isPublicationValid(this.localForm.publications[index])) {
        this.publicationAttempted = {
          ...this.publicationAttempted,
          [index]: false
        }
      }
    },

    async savePublication(index) {
      if (!this.isPublicationValid(this.localForm.publications[index])) {
        this.publicationAttempted = {
          ...this.publicationAttempted,
          [index]: true
        }
        return
      }

      try {
        const publicationsData = { publications: this.publicationsForApi() }
        const response = await ProfileApiService.updateProfile(publicationsData)

        // Refresh form data with latest from API response
        if (response?.data?.publications) {
          this.localForm.publications = response.data.publications
        }

        this.publicationDirty = {
          ...this.publicationDirty,
          [index]: false
        }
        this.updateParent()
      } catch (error) {
        console.error('Error saving publication:', error)
        this.$emit('error', 'Failed to save publication. Please try again.')
      }
    },

    removePublication(index) {
      const removed = this.localForm.publications[index]
      this.localForm.publications.splice(index, 1)
      const removedKey = this.publicationKey(removed, index)
      this.expandedPublicationIds = this.expandedPublicationIds.filter((id) => id !== removedKey)
      const nextDirty = {}
      Object.keys(this.publicationDirty).forEach((key) => {
        const dirtyIndex = Number(key)
        if (dirtyIndex < index) {
          nextDirty[dirtyIndex] = this.publicationDirty[key]
        }
        if (dirtyIndex > index) {
          nextDirty[dirtyIndex - 1] = this.publicationDirty[key]
        }
      })
      this.publicationDirty = nextDirty
      this.updateParent()
    },

    publicationKey(publication, index) {
      return publication?.id || publication?._localKey || `publication-${index}`
    },

    togglePublication(key) {
      if (!key) return

      if (this.expandedPublicationIds.includes(key)) {
        this.expandedPublicationIds = this.expandedPublicationIds.filter((id) => id !== key)
      } else {
        this.expandedPublicationIds.push(key)
      }
      this.$nextTick(() => this.hydrateEditors())
    },

    isPublicationExpanded(key) {
      return this.expandedPublicationIds.includes(key)
    },

    initializeExpandedPublicationIds() {
      this.reconcileExpandedPublicationIds(this.expandedPublicationIds)
    },

    shouldSyncFromFormData(newData) {
      if (!newData) return false

      const incomingPublications = Array.isArray(newData.publications) ? newData.publications : []

      if (!this.initialized) return true

      // Keep local editing state stable; only resync for clear/reset scenarios.
      return incomingPublications.length === 0 && this.localForm.publications.length > 0
    },

    shouldSyncFromProfileData() {
      if (!this.initialized) return true

      const profilePublications = this.fellowDetailsPublications
      const hasProfilePublications = Array.isArray(profilePublications) && profilePublications.length > 0

      // If local form is empty but profile data exists, allow backfill.
      return this.localForm.publications.length === 0 && hasProfilePublications
    },

    reconcileExpandedPublicationIds(previousExpandedIds = []) {
      const availableKeys = new Set((this.localForm.publications || []).map((publication, index) => this.publicationKey(publication, index)))
      this.expandedPublicationIds = previousExpandedIds.filter((id) => availableKeys.has(id))
    },

    normalizeHeaderText(value) {
      if (typeof value !== 'string') return ''
      const cleaned = value.trim().replace(/\s+/g, ' ')
      if (!cleaned) return ''

      return cleaned
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    getYear(value) {
      if (!value) return ''
      const asString = String(value).trim()
      const match = asString.match(/^(\d{4})/)
      return match ? match[1] : ''
    },

    addAuthor(publicationIndex) {
      this.localForm.publications[publicationIndex].authors.push({
        name: '',
        citation: ''
      })
      this.markPublicationDirty(publicationIndex)
    },

    removeAuthor(publicationIndex, authorIndex) {
      this.localForm.publications[publicationIndex].authors.splice(authorIndex, 1)
      this.markPublicationDirty(publicationIndex)
    },

    addExternalIdentifier(publicationIndex) {
      this.localForm.publications[publicationIndex].externalIdentifiers.push({
        identifier: '',
        externalId: '',
        fullUrl: '',
        relationship: ''
      })
      this.markPublicationDirty(publicationIndex)
    },

    removeExternalIdentifier(publicationIndex, identifierIndex) {
      this.localForm.publications[publicationIndex].externalIdentifiers.splice(identifierIndex, 1)
      this.markPublicationDirty(publicationIndex)
    },

    async syncOrcidPublications() {
      if (!this.orcidId || this.syncingOrcid) return

      this.syncingOrcid = true
      this.orcidSyncMessage = ''
      this.orcidSyncError = false

      try {
        const response = await ProfileApiService.syncOrcidPublications()

        // ApiService.post() resolves (rather than throws) for statuses it has no
        // special case for, e.g. the 502 the backend returns when ORCID itself is
        // unreachable, so `success: false` still has to be checked explicitly here
        // - otherwise that failure message renders in the green/success alert.
        if (response?.success === false) {
          this.orcidSyncError = true
          this.orcidSyncMessage = response.message || 'Failed to sync publications from ORCID. Please try again.'
          return
        }

        if (Array.isArray(response?.publications)) {
          this.localForm.publications = response.publications.map((publication) => ({
            id: publication.id || null,
            _localKey: this.generateLocalKey(),
            _isNew: false,
            title: publication.title || '',
            authors: Array.isArray(publication.authors) ? publication.authors : [],
            externalIdentifiers: Array.isArray(publication.externalIdentifiers) ? publication.externalIdentifiers : [],
            journal: publication.journal || '',
            keywords: publication.keywords || '',
            date: publication.date || '',
            content: publication.content || '',
            // Carried through (but not otherwise edited in this form) so that a
            // later "Save" round-trips them back to the server unchanged. Without
            // these, the next general profile save would drop orcid_work/pm_id
            // from the saved record, and a subsequent sync would then treat the
            // same ORCID work as new and duplicate it.
            ...(publication.orcid_work ? { orcid_work: publication.orcid_work } : {}),
            ...(publication.pm_id ? { pm_id: publication.pm_id } : {}),
            ...(publication.citation ? { citation: publication.citation } : {})
          }))
          this.reconcileExpandedPublicationIds(this.expandedPublicationIds)
          this.hydrateEditors()
          this.updateParent()
        }

        this.orcidSyncMessage = response?.message || 'Synced publications from ORCID.'
      } catch (error) {
        console.error('ORCID sync error:', error)
        this.orcidSyncError = true
        this.orcidSyncMessage = error?.message || 'Failed to sync publications from ORCID. Please try again.'
      } finally {
        this.syncingOrcid = false
      }
    }
  },

  mounted() {
    this.initialized = (this.localForm.publications || []).length > 0
    this.initializeExpandedPublicationIds()
    this.hydrateEditors()
  }
}
</script>

<style scoped>
h6.publication-header {
  color: #495057;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-base);
  margin-bottom: 0;
}

.h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}

.publications-form {
  min-height: 400px;
}

/* === Section Header (same as ProjectsForm) === */
.section-header {
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* === Publication Container === */
.publication-card {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.publication-list-row {
  gap: 0.75rem;
}

.publication-list-summary {
  min-width: 0;
}

.publication-row-actions {
  flex-shrink: 0;
}

/* Remove card header background, align with Projects look */
.card-header {
  background-color: transparent;
  border-bottom: none;
  padding: 0;
  margin-bottom: 1rem;
}

.card-title {
  font-weight: var(--font-weight-semibold);
  color: #495057;
  font-size: var(--font-size-base);
  margin: 0;
}

/* === Input + Select Fields === */
.form-label {
  font-weight: var(--font-weight-medium);
  margin-bottom: 0.5rem;
  color: #495057;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

/* === Subsections (Authors, External Identifiers) === */
.subsection {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.nested-item {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
}

/* Nested Titles (Author 1, etc.) */
.nested-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.nested-title {
  font-weight: var(--font-weight-medium);
  color: #495057;
}

/* === Buttons (identical to ProjectsForm) === */
.btn-outline-primary {
  color: #17a2b8;
  border-color: #17a2b8;
  font-size: var(--font-size-sm);
}

.btn-outline-primary:hover {
  background-color: #17a2b8;
  color: #fff;
}

.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}

.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}

.btn-link {
  color: #dc3545;
  text-decoration: none;
  padding: 0.25rem;
}

.btn-link:hover {
  color: #a71d2a;
}

/* === Content Editor (simplified to match textarea style) === */
.content-editor {
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.editor-toolbar {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0.5rem;
}

.content-textarea {
  border: none;
  padding: 0.75rem;
  resize: vertical;
  font-size: var(--font-size-sm);
}

.content-textarea:focus {
  outline: none;
  box-shadow: none;
}

/* === Empty State === */
.empty-state {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background-color: #f8f9fa;
  color: #6c757d;
}

.text-muted {
  font-size: var(--font-size-sm);
}
</style>
